import type { AnyRoute, AnyRouter } from '@tanstack/react-router';

/**
 * How long a route's code may go with no script or stylesheet arriving before its load counts as
 * stalled. The boot stall guard's first-attempt budget (`BOOT_STALL_QUIET_MS` in
 * `client/index.html`): arriving code restarts the period, so a slow connection that keeps
 * completing files is never cut off, and only a request that has stopped answering is.
 */
export const ROUTE_CHUNK_STALL_QUIET_MS = 30_000;
/**
 * The longest a route's code may take however much other code keeps arriving: the idle and hover
 * preloaders can complete unrelated chunks for minutes while this one hangs. The boot stall
 * guard's largest budget (its third attempt).
 */
export const ROUTE_CHUNK_STALL_CEILING_MS = 120_000;
/**
 * How long a route's stall gives its next wait more time: long enough to read the error view and
 * reload, short enough that an old incident does not hold back the error for the next.
 */
export const ROUTE_CHUNK_STALL_BACKOFF_MS = 10 * 60_000;
/** Each recent stall of a route doubles both budgets, at most twice: 30, 60, then 120 s quiet. */
const MAX_BACKOFF_STEPS = 2;
/** Not the shell's `curium-chunk-reload`, which picks its reload tiers and clears on mount. */
const BACKOFF_STORAGE_KEY = 'curium-route-chunk-stall';
const POLL_MS = 1_000;
/**
 * The shortest gap between polls that counts as the page not running, whatever the quiet budget: a
 * poll at least a whole poll late. Only a budget shorter than this (a test's) needs the floor.
 */
const MIN_SUSPENDED_GAP_MS = 2 * POLL_MS;
const MAX_UNANSWERED_LISTED = 3;

/** The build's chunks and stylesheets (`/assets/*.js`, `/assets/*.css`). */
const CODE_PATHNAME = /\.(?:js|css)$/;

const STALL_GUARD = Symbol('route-chunk-stall-guard');

type Preload = () => Promise<unknown> | undefined;
type GuardedPreload = Preload & { [STALL_GUARD]?: true };
type SplitComponent = { preload?: GuardedPreload };

/**
 * Which budget ran out: `quiet` when no script or stylesheet arrived for the quiet budget, `ceiling`
 * when other code kept arriving but this route's still had not after the ceiling.
 */
export type RouteChunkStallReason = 'quiet' | 'ceiling';

type RouteChunkStall = {
    reason: RouteChunkStallReason;
    /** The budget that ran out. */
    budgetMs: number;
    /** How long the page had spent running while the route's code loaded, less any suspension. */
    elapsedMs: number;
    unansweredAssets: readonly string[];
};

function stallMessage(routeId: string, reason: RouteChunkStallReason, budgetMs: number): string {
    const seconds = Math.round(budgetMs / 1000);
    return reason === 'quiet'
        ? `The code for ${routeId} stopped arriving: no script loaded for ${seconds}s`
        : `The code for ${routeId} was still not loaded after ${seconds}s`;
}

/**
 * A route's code stopped arriving: no 503 and no error event, the request simply never answered.
 * The message names the route and the budget that ran out only. The unanswered paths travel on
 * `unansweredAssets`, because a hashed filename in a message that reached `unhandledrejection`
 * would trip the chunk-error reload ladder in `client/index.html`.
 */
export class RouteChunkStallError extends Error {
    /** The route whose code did not load, such as `/console/$orgId`. */
    readonly routeId: string;
    /** Which budget ran out; a `ceiling` stall happened while other code kept arriving. */
    readonly reason: RouteChunkStallReason;
    /**
     * How long the page had spent running while the route's code loaded when the wait gave up:
     * time the tab was frozen or the machine asleep is left out.
     */
    readonly elapsedMs: number;
    /** Preloaded app scripts and stylesheets with no completed response, at most three; for diagnosis only. */
    readonly unansweredAssets: readonly string[];

    constructor(routeId: string, { reason, budgetMs, elapsedMs, unansweredAssets }: RouteChunkStall) {
        super(stallMessage(routeId, reason, budgetMs));
        this.name = 'RouteChunkStallError';
        this.routeId = routeId;
        this.reason = reason;
        this.elapsedMs = elapsedMs;
        this.unansweredAssets = unansweredAssets;
    }
}

export type CodeArrivals = {
    /** When a script or stylesheet last finished arriving (`Date.now()` clock), or 0 for never. */
    lastArrivalAt(): number;
    /**
     * Same-origin app scripts and stylesheets linked for preloading (`modulepreload`, and the
     * `stylesheet` links Vite's preload helper awaits before a route module runs) with no completed
     * response, at most three.
     */
    unansweredPreloads(): string[];
};

function sameOriginPathname(href: string): string | undefined {
    try {
        const url = new URL(href, window.location.href);
        return url.origin === window.location.origin ? url.pathname : undefined;
    } catch {
        return undefined;
    }
}

/**
 * Watches scripts and stylesheets finish arriving. A `PerformanceObserver` rather than the
 * resource-timing buffer, which stops recording at 250 entries, well within a long session.
 * Where the observer is unavailable nothing ever counts as arriving, and a wait degrades to a
 * plain timer.
 */
export function observeCodeArrivals(): CodeArrivals {
    let lastArrivalAt = 0;
    const arrived = new Set<string>();
    const record = (entries: PerformanceEntryList) => {
        for (const entry of entries) {
            const pathname = sameOriginPathname(entry.name);
            if (pathname === undefined || !CODE_PATHNAME.test(pathname)) continue;
            arrived.add(pathname);
            lastArrivalAt = Date.now();
        }
    };
    try {
        new PerformanceObserver((list) => record(list.getEntries())).observe({ type: 'resource', buffered: true });
    } catch {
        // Diagnosis and progress are best-effort; the guard must not stop the app from booting.
    }
    return {
        lastArrivalAt: () => lastArrivalAt,
        unansweredPreloads: () => {
            const unanswered = new Set<string>();
            const links = document.querySelectorAll<HTMLLinkElement>(
                'link[rel="modulepreload"], link[rel="stylesheet"]',
            );
            for (const link of links) {
                const pathname = sameOriginPathname(link.href);
                if (pathname === undefined || !CODE_PATHNAME.test(pathname) || arrived.has(pathname)) continue;
                unanswered.add(pathname);
            }
            return [...unanswered].slice(0, MAX_UNANSWERED_LISTED);
        },
    };
}

/**
 * Recent stalls per route, so the wait after a reload has more time than the one it replaced.
 * Resource timing records a file only once it has fully arrived, so a large chunk still
 * downloading on a slow connection looks exactly like one that never answered; with a fixed
 * budget it would be cut off at the same point on every reload.
 */
export type StallBackoff = {
    /** How many times the budget doubles for a wait on this route's code starting now. */
    steps(routeId: string): number;
    /** The route's code stalled during a wait that started with `steps` doublings. */
    recordStall(routeId: string, steps: number): void;
    /** The route's code arrived. */
    recordLoaded(routeId: string): void;
};

type BackoffEntry = { steps: number; at: number };

function readBackoff(storage: Storage): Map<string, BackoffEntry> {
    const entries = new Map<string, BackoffEntry>();
    try {
        const parsed: unknown = JSON.parse(storage.getItem(BACKOFF_STORAGE_KEY) ?? '{}');
        if (typeof parsed !== 'object' || parsed === null) return entries;
        for (const [routeId, value] of Object.entries(parsed)) {
            const steps = Number(value?.steps);
            const at = Number(value?.at);
            const fresh = Date.now() - at <= ROUTE_CHUNK_STALL_BACKOFF_MS;
            if (Number.isInteger(steps) && steps > 0 && Number.isFinite(at) && fresh) {
                entries.set(routeId, { steps: Math.min(steps, MAX_BACKOFF_STEPS), at });
            }
        }
    } catch {
        // Unreadable state starts the ladder again.
    }
    return entries;
}

function writeBackoff(storage: Storage, entries: Map<string, BackoffEntry>): void {
    try {
        if (entries.size === 0) storage.removeItem(BACKOFF_STORAGE_KEY);
        else storage.setItem(BACKOFF_STORAGE_KEY, JSON.stringify(Object.fromEntries(entries)));
    } catch {
        // Without storage every wait has the first budget, as before a stall.
    }
}

/**
 * Keeps recent stalls in `sessionStorage`, which survives the reload the stall view offers (or
 * the browser's own) and stays with the tab. Where storage is unavailable no stall is remembered.
 */
export function sessionStallBackoff(): StallBackoff {
    const storage = () => {
        try {
            return window.sessionStorage;
        } catch {
            return undefined;
        }
    };
    return {
        steps: (routeId) => {
            const store = storage();
            return store ? (readBackoff(store).get(routeId)?.steps ?? 0) : 0;
        },
        recordStall: (routeId, steps) => {
            const store = storage();
            if (!store) return;
            const entries = readBackoff(store);
            // A navigation and a preload sharing the import stall together: one step, not two.
            const next = Math.max(entries.get(routeId)?.steps ?? 0, Math.min(steps + 1, MAX_BACKOFF_STEPS));
            entries.set(routeId, { steps: next, at: Date.now() });
            writeBackoff(store, entries);
        },
        recordLoaded: (routeId) => {
            const store = storage();
            if (!store) return;
            const entries = readBackoff(store);
            if (entries.delete(routeId)) writeBackoff(store, entries);
        },
    };
}

type StallWait = {
    quietMs: number;
    ceilingMs: number;
    arrivals: CodeArrivals;
};

type StallGuard = StallWait & { backoff: StallBackoff };

/**
 * Settles with `load`, or fails with a {@link RouteChunkStallError} once no code has arrived for
 * the quiet budget, measured from whichever is later: the quiet period starting or the last
 * arrival; or once the page has spent the ceiling running since the wait started.
 *
 * While the page runs, both budgets count wall-clock time, so a background tab's throttled timers
 * (a poll every few seconds) cannot stretch either. A gap between polls of at least the quiet
 * budget, and never less than two polls (`MIN_SUSPENDED_GAP_MS`), means the page was not running:
 * a frozen or sleeping tab, a machine asleep (which fires no lifecycle event), or a long-hidden
 * tab's intensive throttling. No import could resolve and no arrival could be observed in that
 * time, so the gap restarts the quiet period from the poll that ends it and does not count towards
 * the ceiling. The page then has a fresh quiet budget for what
 * the resumed network delivers (CURIUM-FE-XY: a tab returned to after 3 h 46 min failed `/login`
 * on its first poll). The trade-off: under intensive throttling, about one poll a minute, a stall
 * no longer surfaces while the tab stays hidden; it surfaces within the quiet budget once the page
 * runs normally again, which is when someone can see it.
 */
function waitForRouteCode(
    routeId: string,
    load: Promise<unknown>,
    { quietMs, ceilingMs, arrivals }: StallWait,
    onStall: () => void,
): Promise<void> {
    return new Promise((resolve, reject) => {
        const startedAt = Date.now();
        let lastPollAt = startedAt;
        let quietSince = startedAt;
        /** Time the page spent running since the wait started: wall-clock time less suspensions. */
        let elapsedMs = 0;
        const suspendedGapMs = Math.max(quietMs, MIN_SUSPENDED_GAP_MS);
        const poll = setInterval(() => {
            const now = Date.now();
            // A clock set backwards is not time spent running.
            const gap = Math.max(0, now - lastPollAt);
            lastPollAt = now;
            if (gap >= suspendedGapMs) {
                // The page was not running: the quiet period starts again here, and this poll judges nothing.
                quietSince = now;
                return;
            }
            elapsedMs += gap;
            const quietFor = now - Math.max(quietSince, arrivals.lastArrivalAt());
            if (quietFor < quietMs && elapsedMs < ceilingMs) return;
            clearInterval(poll);
            onStall();
            // Quiet first when both have run out: nothing arriving at all is the narrower diagnosis.
            const reason = quietFor >= quietMs ? 'quiet' : 'ceiling';
            reject(
                new RouteChunkStallError(routeId, {
                    reason,
                    budgetMs: reason === 'quiet' ? quietMs : ceilingMs,
                    elapsedMs,
                    unansweredAssets: arrivals.unansweredPreloads(),
                }),
            );
        }, POLL_MS);
        load.then(
            () => {
                clearInterval(poll);
                resolve();
            },
            (error: unknown) => {
                clearInterval(poll);
                reject(error);
            },
        );
    });
}

/**
 * Time-limits the route's split component while its code is cold. Router-core reads
 * `component.preload` on each load (`preloadComponent` in `load-client`), and
 * `lazyRouteComponent` clears it once the import resolves, so the wrapper drops out on success
 * and `isRouteChunkWarm` keeps reading the route correctly. Each wait takes its budgets from the
 * route's recent stalls when it starts: a reload after a stall never has less time than the wait
 * it replaced, up to four times the first budgets.
 */
function guardRoute(route: AnyRoute, { quietMs, ceilingMs, arrivals, backoff }: StallGuard): void {
    const component = route.options.component as SplitComponent | undefined;
    const preload = component?.preload;
    if (!component || typeof preload !== 'function' || preload[STALL_GUARD]) {
        return;
    }
    // Every call while the code is cold waits on its own budgets, from when that call starts. A
    // hover, idle or redirect preload goes through this too, and `route-preloader` treats its
    // stall as a preload that warmed nothing. A stall leaves the wrapper in place: router-core
    // calls `preload` only after the route's `beforeLoad` settles, so a preload started during an
    // asynchronous `beforeLoad` can stall before the navigation's own call, which must still be
    // bounded. Only the import resolving takes the wrapper off.
    const guarded: GuardedPreload = () => {
        const steps = backoff.steps(route.id);
        const wait = { quietMs: quietMs * 2 ** steps, ceilingMs: ceilingMs * 2 ** steps, arrivals };
        return waitForRouteCode(route.id, Promise.resolve(preload()), wait, () =>
            backoff.recordStall(route.id, steps),
        ).then(() => backoff.recordLoaded(route.id));
    };
    guarded[STALL_GUARD] = true;
    component.preload = guarded;
}

/**
 * The routes a pathname matches, root first, or none when the table cannot answer. The deepest
 * branch towards an unknown path is included: its code loads before the not-found page renders.
 */
function matchedRoutes(router: AnyRouter, pathname: string): readonly AnyRoute[] {
    try {
        return router.getMatchedRoutes(pathname)[0];
    } catch {
        return [];
    }
}

/**
 * Fails a navigation whose route code has stopped arriving, instead of leaving it waiting on the
 * import for as long as the browser keeps the request open. The router rethrows the failed match
 * to the nearest error boundary: `AppProvider`'s, around every route below the root, which renders
 * `ErrorFallback` (`RouterErrorComponent` too handles it, at the root). Both report it and offer a
 * reload: "Try again" could not help, because `lazyRouteComponent` keeps waiting on the same
 * request until it settles. A route with an error component of its own catches the stall first and
 * must rethrow it, as the organisation switch (`~change-org.$orgId.tsx`) does.
 *
 * This covers the app after it has mounted. Before that, the boot stall guard in
 * `client/index.html` handles the same failure, and it stands down on `__CURIUM_APP_MOUNTED__`
 * (staging, 2026-10-22 before mount; Checkly, 2026-10-29 after it, when the console layout's
 * `PageLayout` chunk never answered while the other ~160 assets loaded).
 *
 * Each navigation guards the routes it matches on `onBeforeLoad`, which fires before the router
 * loads their code, including on the first document load and when the deferred admin console
 * restarts a navigation against the grown route table. Only the split main component is guarded:
 * no route has a `.lazy.tsx` file or a split pending component (see `route-chunk-warmth.ts`), and
 * error components are not split (`routeCodeSplittingOptions` in `vite.router.ts`), so showing a
 * stall never waits on another request.
 *
 * A stall backs off that route's next waits (`sessionStallBackoff`), so a large chunk on a slow
 * connection gets more time after the reload instead of being cut off at the same point each time.
 *
 * Route code is only split in builds (`shouldEnableRouteAutoCodeSplitting` in `vite.config.ts`),
 * so on the dev server no component offers `preload` and nothing is guarded.
 */
export function guardRouteChunkStalls(
    router: AnyRouter,
    {
        quietMs = ROUTE_CHUNK_STALL_QUIET_MS,
        ceilingMs = ROUTE_CHUNK_STALL_CEILING_MS,
        arrivals = observeCodeArrivals(),
        backoff = sessionStallBackoff(),
    }: Partial<StallGuard> = {},
): () => void {
    const guard = { quietMs, ceilingMs, arrivals, backoff };
    return router.subscribe('onBeforeLoad', ({ toLocation }) => {
        for (const route of matchedRoutes(router, toLocation.pathname)) {
            guardRoute(route, guard);
        }
    });
}
